import type {
  DesignSpecFieldChange,
  DesignSpecFieldValue,
} from "@/types/generatedImage";
import type { DesignSpec, DesignSpecReference } from "@/types/designSpec";
import { diffDesignSpecFields } from "@/lib/agent/designSpecSnapshot";
import type { PreviewFreshness } from "./contracts";
import { getGeneratedDesignSpec } from "./savedDesign";

export type DesignPreviewSyncKind = "clean" | "dirty" | "unknown";

const FIELD_LABELS: Record<string, string> = {
  occasion: "Occasion",
  eventDate: "Event date",
  wearerContext: "Wearer details",
  "visual.prompt": "Overall design",
  "visual.primaryColor": "Color",
  "visual.accentColors": "Accent colors",
  "visual.length": "Length",
  "visual.sleeve": "Sleeve",
  "visual.sleeveAddons": "Sleeve details",
  "visual.neckline": "Neckline",
  "visual.waistline": "Waist",
  "visual.silhouette": "Silhouette",
  "visual.fabric": "Fabric",
  "visual.mood": "Mood",
  "visual.details": "Design details",
  "visual.backPrompt": "Back design",
  "visual.backStyle": "Back style",
  "visual.closure": "Back closure",
  "constraints.mustHave": "Must have",
  "constraints.avoid": "Avoid",
  "constraints.comfort": "Comfort",
  "constraints.mobility": "Mobility",
  "constraints.bodyConcerns": "Fit considerations",
  "constraints.modesty": "Coverage",
  "constraints.coverage": "Coverage details",
  "constraints.support": "Support",
  "constraints.opacity": "Opacity",
  "constraints.accessibility": "Accessibility",
  "constraints.cultural": "Cultural details",
  "constraints.construction": "Construction",
  "constraints.budget": "Budget",
  references: "Reference images",
  referenceGuidance: "Reference guidance",
};

const BACK_ONLY_PATHS = new Set([
  "visual.backPrompt",
  "visual.backStyle",
  "visual.closure",
]);

export function getDesignSpecFieldLabel(path: string): string {
  return (
    FIELD_LABELS[path] ??
    path
      .split(".")
      .at(-1)
      ?.replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, character => character.toUpperCase()) ??
    "Design detail"
  );
}

/**
 * A front render does not become visibly stale when only back-specific fields
 * change. Every other render-facing field can affect both sides.
 */
export function getSideRelevantDesignChanges(
  changes: DesignSpecFieldChange[],
  side: "front" | "back"
): DesignSpecFieldChange[] {
  return changes.flatMap(change => {
    if (side === "front" && BACK_ONLY_PATHS.has(change.path)) return [];
    if (change.path !== "references") return [change];

    const forSide = (value: DesignSpecFieldValue): DesignSpecReference[] =>
      Array.isArray(value)
        ? value.filter(
            (candidate): candidate is DesignSpecReference =>
              typeof candidate === "object" &&
              candidate !== null &&
              "side" in candidate &&
              "role" in candidate &&
              (candidate.side === side || candidate.side === "inspiration") &&
              !(side === "front" && candidate.role === "back")
          )
        : [];
    const renderedValue = forSide(change.renderedValue);
    const draftValue = forSide(change.draftValue);

    return JSON.stringify(renderedValue) === JSON.stringify(draftValue)
      ? []
      : [{ ...change, renderedValue, draftValue }];
  });
}

/**
 * `compileDesignSpec` reconciles the prose prompt whenever a structured visual
 * field changes. Show the concrete field instead of presenting that derived
 * prompt rewrite as a second customer-authored change.
 */
export function getPresentableDesignChanges(
  changes: DesignSpecFieldChange[]
): DesignSpecFieldChange[] {
  const hasConcreteVisualChange = changes.some(
    change =>
      change.path.startsWith("visual.") && change.path !== "visual.prompt"
  );
  return hasConcreteVisualChange
    ? changes.filter(change => change.path !== "visual.prompt")
    : changes;
}

export function buildDesignPreviewUpdatePrompt(
  side: "front" | "back",
  changes: DesignSpecFieldChange[]
): string {
  const focusedChanges = getPresentableDesignChanges(
    getSideRelevantDesignChanges(changes, side)
  );
  if (!focusedChanges.length) {
    return `Update the existing ${side} dress preview to the exact values in the attached current design specification. Preserve visible details that the specification does not change. The attached specification is authoritative.`;
  }
  // The frozen contract carries exact desired values. Repeating old values or
  // serialized Avoid lists here can make the conflict validator interpret a
  // removed feature as a new positive request (e.g. "Avoid: ... cutout").
  const compact = [
    `Update the existing ${side} dress preview to the exact values in the attached current design specification.`,
    `Changed fields: ${[...new Set(focusedChanges.map(change => getDesignSpecFieldLabel(change.path)))].join(", ")}.`,
    "Preserve every other visible detail from the existing image. The attached current design specification is authoritative.",
  ].join("\n");
  return compact.length <= 1000
    ? compact
    : `Apply the attached current design specification to the existing ${side} dress preview, preserving visible details that the specification does not change.`;
}

export function changedFieldSummary(
  changes: DesignSpecFieldChange[],
  maximumVisibleFields = 3
): string {
  const labels = getPresentableDesignChanges(changes).map(change =>
    getDesignSpecFieldLabel(change.path)
  );
  const uniqueLabels = [...new Set(labels)];
  const visibleLabels = uniqueLabels.slice(0, maximumVisibleFields);
  const remaining = uniqueLabels.length - visibleLabels.length;
  return `${visibleLabels.join(", ")}${remaining > 0 ? ` +${remaining} more` : ""}`;
}

/** One freshness projection for the browser and terminal lab; no React dependencies. */
export function projectPreviewFreshness(input: {
  side: "front" | "back";
  hasImage: boolean;
  renderedSpec: DesignSpec | null | undefined;
  draftSpec: DesignSpec | null | undefined;
}): { freshness: PreviewFreshness; changes: DesignSpecFieldChange[] } {
  if (!input.hasImage) return { freshness: "empty", changes: [] };
  if (!input.renderedSpec || !input.draftSpec)
    return { freshness: "unknown", changes: [] };
  const changes = getSideRelevantDesignChanges(
    diffDesignSpecFields(input.renderedSpec, input.draftSpec),
    input.side
  );
  return {
    freshness: changes.length > 0 ? "changes_pending" : "current",
    changes,
  };
}

/**
 * A Back preview is based on the Front that existed when it was rendered.
 * Compare the two saved render contracts, rather than the editable draft, so a
 * later Front revision leaves a durable Back reminder without buying another
 * render or treating uncommitted edits as already saved.
 */
export function projectSavedBackStaleness(
  images: Record<
    "front" | "back",
    { url: string | null; path?: string | null; designSpec: DesignSpec | null }
  >
): { stale: boolean; changes: DesignSpecFieldChange[] } {
  if (!images.back.url) return { stale: false, changes: [] };
  const saved = projectPreviewFreshness({
    side: "back",
    hasImage: true,
    renderedSpec: images.back.designSpec,
    draftSpec: getGeneratedDesignSpec(images),
  });
  const changes = getPresentableDesignChanges(saved.changes);
  const sourceFrontPath = images.back.designSpec?.renderSource?.frontImagePath;
  // Old previews may have a render contract without recording which Front
  // image they used, or no contract at all. A visible pair whose lineage
  // cannot be verified must not be submitted as a matching two-sided dress.
  const sourceUnverified =
    !images.front.designSpec ||
    !images.back.designSpec ||
    !sourceFrontPath ||
    !images.front.path;
  const sourceImageChanged =
    Boolean(sourceFrontPath && images.front.path) &&
    sourceFrontPath !== images.front.path;
  return {
    stale: changes.length > 0 || sourceUnverified || sourceImageChanged,
    changes,
  };
}
